import { API_CONFIG, getAuthHeaders, handleApiError, ApiResponse } from './apiConfig'

// Helper pour fetch avec configuration CORS
function fetchWithCors(url: string, options: RequestInit = {}): Promise<Response> {
  return fetch(url, {
    ...options,
    mode: 'cors',
    credentials: 'include',
    headers: {
      ...API_CONFIG.headers,
      ...options.headers
    }
  })
}

// Interfaces
export interface WalletTransaction {
  id: number
  reference: string
  description: string
  categorieSlug: string
  montant: number
  type: string // 'credit' | 'debit'
  devise: string
  libelle: string
  walletId: number | null
  voirWallet: boolean
  statut: string
  date: string
  metadata: any
  userId: number | string
  userNom: string
}

export interface MoisAggregate {
  mois: number
  annee: number
  montant: number
  nombre: number
  montant_credit: number
  nombre_credit: number
  montant_debit: number
  nombre_debit: number
  transactions: WalletTransaction[]
}

export interface PaginatedTransactions {
  current_page: number
  data: WalletTransaction[]
  total: number
  per_page: number
  last_page: number
}

// Service Wallet
const walletService = {

  // Récupérer le solde du wallet
  async getBalance(): Promise<ApiResponse<{ balance: number }>> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/wallet/balance`, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      const data = await response.json()
      if (!response.ok) {
        return handleApiError({ response: { status: response.status, data } })
      }
      return { status: response.status, message: 'Balance récupérée', data: data }
    } catch (error) {
      return handleApiError(error)
    }
  },

  // Récupérer les transactions paginées
  async getTransactions(params?: {
    per_page?: number
    search?: string
    walletId?: number | null
    dateDebut?: string
    dateFin?: string
    page?: number
  }): Promise<ApiResponse<PaginatedTransactions>> {
    try {
      const queryParts: string[] = []
      if (params?.per_page) queryParts.push(`per_page=${params.per_page}`)
      if (params?.search) queryParts.push(`search=${encodeURIComponent(params.search)}`)
      if (params?.walletId !== undefined && params?.walletId !== null) queryParts.push(`walletId=${params.walletId}`)
      if (params?.dateDebut) queryParts.push(`dateDebut=${params.dateDebut}`)
      if (params?.dateFin) queryParts.push(`dateFin=${params.dateFin}`)
      if (params?.page) queryParts.push(`page=${params.page}`)
      const queryString = queryParts.length > 0 ? '?' + queryParts.join('&') : ''

      const response = await fetchWithCors(`${API_CONFIG.baseURL}/transactions${queryString}`, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      const data = await response.json()
      if (!response.ok) {
        return handleApiError({ response: { status: response.status, data } })
      }
      return { status: response.status, message: 'Transactions récupérées', data: data }
    } catch (error) {
      return handleApiError(error)
    }
  },

  // Récupérer les transactions groupées par mois
  async getTransactionsMois(params?: {
    mois?: number
    annee?: number
    walletId?: number | null
  }): Promise<ApiResponse<MoisAggregate[]>> {
    try {
      const queryParts: string[] = []
      if (params?.mois) queryParts.push(`mois=${params.mois}`)
      if (params?.annee) queryParts.push(`annee=${params.annee}`)
      if (params?.walletId !== undefined && params?.walletId !== null) queryParts.push(`walletId=${params.walletId}`)
      const queryString = queryParts.length > 0 ? '?' + queryParts.join('&') : ''

      const response = await fetchWithCors(`${API_CONFIG.baseURL}/transactions/mois${queryString}`, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      const data = await response.json()
      if (!response.ok) {
        return handleApiError({ response: { status: response.status, data } })
      }
      return { status: response.status, message: 'Transactions par mois récupérées', data: data.data || data }
    } catch (error) {
      return handleApiError(error)
    }
  },

  // Récupérer une transaction
  async getTransaction(id: number): Promise<ApiResponse<WalletTransaction>> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/transactions/${id}`, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      const data = await response.json()
      if (!response.ok) {
        return handleApiError({ response: { status: response.status, data } })
      }
      return { status: response.status, message: 'Transaction récupérée', data: data.data || data }
    } catch (error) {
      return handleApiError(error)
    }
  }
}

export default walletService
